import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAIScribe } from '@/hooks/ai';
import { useAuth } from '@/contexts/AuthContext';
import { SOAPNote } from '@/api/ai-api';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';

export function SOAPScribePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mutateAsync: generateSOAP, isPending, data: result, error } = useAIScribe();

  const [appointmentId, setAppointmentId] = useState('');
  const [patientId, setPatientId] = useState('');
  const [transcriptText, setTranscriptText] = useState('');

  const handleGenerate = async () => {
    if (!user?.id || !appointmentId || !patientId) return;
    const transcript = transcriptText
      .split('\n')
      .filter(line => line.trim())
      .map(line => {
        const [speaker, ...rest] = line.split(':');
        return {
          speaker: (speaker?.toLowerCase().trim() === 'doctor' ? 'doctor' : speaker?.toLowerCase().trim() === 'system' ? 'system' : 'patient') as 'doctor' | 'patient' | 'system',
          text: rest.join(':').trim(),
        };
      });

    await generateSOAP({ appointmentId, patientId, doctorId: user.id, transcript });
  };

  return (
    <div className="container max-w-6xl py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SOAP Scribe IA</h1>
          <p className="text-gray-500 mt-1">Agent 03 — Transcrição de consulta em registro SOAP estruturado</p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>Voltar</Button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">Entrada</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Appointment ID</label>
              <input
                type="text"
                value={appointmentId}
                onChange={e => setAppointmentId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="UUID da consulta"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Patient ID</label>
              <input
                type="text"
                value={patientId}
                onChange={e => setPatientId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="UUID do paciente"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Transcrição (formato: <code>speaker: texto</code> por linha)
              </label>
              <Textarea
                value={transcriptText}
                onChange={e => setTranscriptText(e.target.value)}
                rows={12}
                placeholder={'doctor: Bom dia, o que trouxe você aqui?\npatient: Estou com dor de cabeça há 3 dias\ndoctor: Vou examinar...'}
              />
            </div>

            <Button onClick={handleGenerate} disabled={isPending || !transcriptText || !patientId || !appointmentId}>
              {isPending ? 'Gerando SOAP...' : 'Gerar Nota SOAP'}
            </Button>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                Erro: {(error as Error).message}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">Resultado</h2>

          {result?.soap ? <SOAPView soap={result.soap} /> : (
            <div className="text-sm text-gray-500 text-center py-8">
              O registro SOAP estruturado aparecerá aqui
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SOAPView({ soap }: { soap: SOAPNote }) {
  return (
    <div className="space-y-3 text-sm">
      <Section title="S — Subjetivo" content={soap.subjective} color="blue" />
      <Section title="O — Objetivo" content={soap.objective} color="green" />
      <Section title="A — Avaliação" content={soap.assessment} color="orange" />
      <Section title="P — Plano" content={soap.plan} color="purple" />

      {soap.metadata.diagnoses.length > 0 && (
        <div className="bg-gray-50 rounded p-3 border border-gray-200 mt-3">
          <div className="text-xs font-semibold text-gray-600 mb-1">Diagnósticos sugeridos</div>
          <ul className="text-xs text-gray-700">
            {soap.metadata.diagnoses.map((d, i) => (
              <li key={i}>{d.description} {d.cidCode && `(${d.cidCode})`}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Section({ title, content, color }: { title: string; content: string; color: string }) {
  const colors: Record<string, string> = {
    blue: 'border-blue-300 bg-blue-50',
    green: 'border-green-300 bg-green-50',
    orange: 'border-orange-300 bg-orange-50',
    purple: 'border-purple-300 bg-purple-50',
  };
  return (
    <div className={`rounded p-3 border-l-4 ${colors[color] || colors.blue}`}>
      <div className="text-xs font-semibold uppercase mb-1">{title}</div>
      <div className="text-sm text-gray-800 whitespace-pre-wrap">{content || '(vazio)'}</div>
    </div>
  );
}